import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Card, CardBody } from "reactstrap";

import ProfessionalAvatar from "../../../Components/Common/ProfessionalAvatar";
import {
  PublicBookingAppointment,
  PublicBookingClubTeaser,
  PublicBookingProfessional,
  PublicBookingService,
} from "../../../services/publicBookingService";

interface SuccessStepProps {
  appointment: PublicBookingAppointment;
  service: PublicBookingService;
  professional: PublicBookingProfessional;
  clubTeaser?: PublicBookingClubTeaser | null;
}

const formatDateLabel = (datetime: string): string => {
  const [datePart] = datetime.split(" ");
  const [year, month, day] = datePart.split("-");

  return `${day}/${month}/${year}`;
};

const formatTimeLabel = (datetime: string): string => datetime.slice(11, 16);

const formatMoneyFromCents = (cents: number, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  } catch {
    return `R$ ${(cents / 100).toFixed(2)}`;
  }
};

const clubPathFromUrl = (url: string | null | undefined): string | null => {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url.startsWith("/") ? url : null;
  }
};

const SuccessStep: React.FC<SuccessStepProps> = ({
  appointment,
  service,
  professional,
  clubTeaser,
}) => {
  const { t, i18n } = useTranslation();

  const clubHref =
    clubPathFromUrl(clubTeaser?.join_url) ||
    clubPathFromUrl(clubTeaser?.plans_url);

  const plansPreview = clubTeaser?.plans_preview ?? [];

  return (
    <div className="text-center py-2">
      <div className="avatar-md mx-auto mb-3">
        <span className="avatar-title rounded-circle bg-success-subtle text-success fs-24">
          <i className="ri-check-line" />
        </span>
      </div>

      <h4 className="mb-2">{t("public_booking.success.title")}</h4>
      <p className="text-muted mb-4">{t("public_booking.success.description")}</p>

      <Card className="border bg-light shadow-none text-start">
        <CardBody>
          <p className="text-muted mb-2">{t("public_booking.summary.attendingLabel")}</p>
          <div className="d-flex align-items-center gap-3 mb-3">
            <ProfessionalAvatar
              name={professional.name}
              colorHex={professional.color_hex}
              photoUrl={professional.photo_url}
              size={56}
            />
            <div>
              <h5 className="fs-16 mb-1">{professional.name}</h5>
              <p className="text-muted mb-0">{service.name}</p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-3">
            <div>
              <small className="text-muted d-block">{t("public_booking.summary.date")}</small>
              <span className="fw-medium">{formatDateLabel(appointment.start_datetime)}</span>
            </div>
            <div>
              <small className="text-muted d-block">{t("public_booking.summary.time")}</small>
              <span className="fw-medium">
                {formatTimeLabel(appointment.start_datetime)}
                {" – "}
                {formatTimeLabel(appointment.end_datetime)}
              </span>
            </div>
            <div>
              <small className="text-muted d-block">{t("public_booking.summary.price")}</small>
              <span className="fw-medium text-primary">{service.price_display}</span>
              {service.price_is_from ? (
                <small className="text-muted d-block mt-1">
                  {t("public_booking.summary.priceFromHint")}
                </small>
              ) : null}
            </div>
          </div>
        </CardBody>
      </Card>

      {clubTeaser?.has_plans && clubHref ? (
        <div className="mt-4 text-start border rounded p-3">
          <h5 className="fs-15 mb-1">{t("public_booking.clubTeaser.title")}</h5>
          <p className="text-muted small mb-3">{t("public_booking.clubTeaser.description")}</p>
          {plansPreview.length > 0 ? (
            <ul className="list-unstyled mb-3">
              {plansPreview.map((plan) => (
                <li
                  key={plan.id}
                  className="d-flex justify-content-between gap-2 small py-1 border-bottom border-light"
                >
                  <span>{plan.name}</span>
                  <span className="text-primary fw-medium text-nowrap">
                    {formatMoneyFromCents(plan.price, i18n.language)}
                    <span className="text-muted fw-normal">
                      {" "}
                      / {t("public_club.perMonth")}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <Button color="primary" tag={Link} to={clubHref} className="w-100">
            {t("public_booking.clubTeaser.cta")}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default SuccessStep;
